import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from '@emotion/styled';
import { ChevronDown, Check, RefreshCw, Pencil } from 'lucide-react';
import API_ENDPOINTS from '../../../config/api';
import { useTranslation } from '../../../hooks/useTranslation';

const Card = styled.div`
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.8), rgba(34, 34, 34, 0.6));
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 20px;
  max-width: 600px;
`;

const FutureSelector = styled.div`
  position: relative;
`;

const SelectorLabel = styled.label`
  display: block;
  font-size: 11px;
  color: #888;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SelectorButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid ${p => p.$open ? '#6366f1' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 10px;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:hover { border-color: ${p => p.$open ? '#6366f1' : 'rgba(255, 255, 255, 0.2)'}; }

  svg {
    transition: transform 0.15s ease;
    transform: ${p => p.$open ? 'rotate(180deg)' : 'rotate(0deg)'};
    opacity: 0.6;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  z-index: 100;
  max-height: 400px;
  overflow-y: auto;
`;

const PerspectiveOption = styled.button`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 12px 14px;
  background: ${p => p.$active ? 'rgba(99, 102, 241, 0.15)' : 'transparent'};
  border: none;
  color: #fff;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:hover { background: rgba(99, 102, 241, 0.1); }
  &:not(:last-child) { border-bottom: 1px solid rgba(255, 255, 255, 0.05); }
`;

const PerspectiveTitle = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  font-size: 13px;
  font-weight: 500;
`;

const PerspectiveDescription = styled.span`
  font-size: 11px;
  color: #888;
  line-height: 1.4;
`;

const SelectedPerspective = styled.div`
  margin-top: 14px;
  padding: 14px;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 10px;
`;

const SelectedHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const SelectedLabel = styled.div`
  font-size: 10px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 11px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover { background: rgba(255, 255, 255, 0.15); color: #fff; }
`;

const EditTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(99, 102, 241, 0.4);
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-family: inherit;
  line-height: 1.5;
  resize: vertical;
  outline: none;
  transition: border-color 0.15s ease;

  &:focus { border-color: #6366f1; }
`;

const EditActions = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 8px;
  justify-content: flex-end;
`;

const ActionBtn = styled.button`
  padding: 5px 14px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 1px solid;
`;

const SaveBtn = styled(ActionBtn)`
  background: rgba(99, 102, 241, 0.2);
  border-color: rgba(99, 102, 241, 0.4);
  color: #818cf8;
  &:hover { background: rgba(99, 102, 241, 0.3); }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const CancelBtn = styled(ActionBtn)`
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
  &:hover { background: rgba(255, 255, 255, 0.1); color: #fff; }
`;

const SelectedTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 6px;
`;

const SelectedDesc = styled.div`
  font-size: 12px;
  color: #aaa;
  line-height: 1.5;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: ${p => p.$saving ? '#ffaa00' : p.$saved ? '#22c55e' : '#666'};
  margin-top: 6px;

  svg {
    animation: ${p => p.$saving ? 'spin 1s linear infinite' : 'none'};
  }

  @keyframes spin { to { transform: rotate(360deg); } }
`;

// Perspectives loaded from database per instance

/**
 * TopicFocusContent - Perspective/topic focus selector for workshop phase.
 * Extracted from WorkshopControls.jsx for use inside PhaseSection.
 */
export default function TopicFocusContent({ instanceId, currentPerspective: propPerspective, locked = false }) {
  const { t } = useTranslation();
  const [perspectives, setPerspectives] = useState([]);
  const [selectedPerspective, setSelectedPerspective] = useState(propPerspective || null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [saving, setSaving] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const textareaRef = useRef(null);

  // Load perspectives from database
  useEffect(() => {
    if (!instanceId) return;

    const load = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.PERSPECTIVES(instanceId));
        if (!res.ok) return;
        const data = await res.json();
        const items = Array.isArray(data) ? data : [];
        setPerspectives(items);
        if (!propPerspective) {
          const selected = items.find(p => p.is_selected);
          if (selected) setSelectedPerspective(selected);
        }
      } catch (err) {
        console.error('Failed to load perspectives:', err);
      }
    };
    load();
  }, [instanceId, propPerspective]);

  const handleSelect = useCallback(async (perspective) => {
    if (locked) { setDropdownOpen(false); return; }
    setDropdownOpen(false);
    setSelectedPerspective(perspective);
    setSaving('saving');

    try {
      const response = await fetch(API_ENDPOINTS.PERSPECTIVES_SELECT(instanceId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ perspectiveId: perspective?.id || null }),
      });
      if (!response.ok) throw new Error('Failed to save');

      const updated = await response.json();
      setPerspectives(Array.isArray(updated) ? updated : []);
      setSaving('saved');
      setTimeout(() => setSaving(null), 2000);
    } catch (error) {
      console.error('Failed to save perspective:', error);
      setSaving(null);
    }
  }, [instanceId, locked]);

  const handleStartEdit = useCallback(() => {
    if (locked) return;
    if (!selectedPerspective) return;
    setEditText(selectedPerspective.custom_title || selectedPerspective.title);
    setEditing(true);
    setTimeout(() => textareaRef.current?.focus(), 50);
  }, [selectedPerspective, locked]);

  const handleSaveEdit = useCallback(async () => {
    if (locked) return;
    if (!selectedPerspective || !editText.trim()) return;
    const trimmed = editText.trim();
    // If edited text matches original, clear custom_title
    const customTitle = trimmed === selectedPerspective.title ? null : trimmed;

    setSaving('saving');
    try {
      const res = await fetch(API_ENDPOINTS.PERSPECTIVE_UPDATE(selectedPerspective.id), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ custom_title: customTitle }),
      });
      if (!res.ok) throw new Error('Failed to update');
      const updated = await res.json();
      setSelectedPerspective(updated);
      setPerspectives(prev => prev.map(p => p.id === updated.id ? updated : p));
      setEditing(false);
      setSaving('saved');
      setTimeout(() => setSaving(null), 2000);
    } catch (error) {
      console.error('Failed to update perspective:', error);
      setSaving(null);
    }
  }, [selectedPerspective, editText, locked]);

  // Close on outside click
  useEffect(() => {
    const handler = () => setDropdownOpen(false);
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <Card>
      <FutureSelector>
        <SelectorLabel>{t('topicFocus.selectedLabel')}</SelectorLabel>
        <SelectorButton
          $open={dropdownOpen}
          onClick={(e) => { e.stopPropagation(); setDropdownOpen(!dropdownOpen); }}
        >
          <span>
            {selectedPerspective
              ? `${selectedPerspective.order_index}. ${selectedPerspective.title}`
              : t('topicFocus.noneSelected')}
          </span>
          <ChevronDown size={16} />
        </SelectorButton>

        {dropdownOpen && (
          <Dropdown onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
            <PerspectiveOption $active={!selectedPerspective} onClick={() => handleSelect(null)}>
              <PerspectiveTitle>
                <span>{t('topicFocus.noFilter')}</span>
                {!selectedPerspective && <Check size={16} style={{ color: '#6366f1' }} />}
              </PerspectiveTitle>
              <PerspectiveDescription>{t('topicFocus.noFilterDescription')}</PerspectiveDescription>
            </PerspectiveOption>
            {perspectives.map(p => (
              <PerspectiveOption key={p.id} $active={selectedPerspective?.id === p.id} onClick={() => handleSelect(p)}>
                <PerspectiveTitle>
                  <span>{p.order_index}. {p.title}</span>
                  {selectedPerspective?.id === p.id && <Check size={16} style={{ color: '#6366f1' }} />}
                </PerspectiveTitle>
                {p.description && <PerspectiveDescription>{p.description}</PerspectiveDescription>}
              </PerspectiveOption>
            ))}
          </Dropdown>
        )}

        {saving && (
          <StatusIndicator $saving={saving === 'saving'} $saved={saving === 'saved'}>
            {saving === 'saving' ? <><RefreshCw size={12} /> {t('topicFocus.saving')}</> : <><Check size={12} /> {t('topicFocus.saved')}</>}
          </StatusIndicator>
        )}
      </FutureSelector>

      {selectedPerspective && (
        <SelectedPerspective>
          <SelectedHeader>
            <SelectedLabel>{t('topicFocus.focusLabel')}</SelectedLabel>
            {!editing && (
              <EditButton onClick={handleStartEdit}>
                <Pencil size={12} /> {t('common.edit')}
              </EditButton>
            )}
          </SelectedHeader>
          {editing ? (
            <>
              <EditTextarea
                ref={textareaRef}
                value={editText}
                onChange={e => setEditText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Escape') setEditing(false); }}
              />
              <EditActions>
                <CancelBtn onClick={() => setEditing(false)}>{t('common.cancel')}</CancelBtn>
                <SaveBtn onClick={handleSaveEdit} disabled={!editText.trim() || saving === 'saving'}>
                  {t('common.save')}
                </SaveBtn>
              </EditActions>
            </>
          ) : (
            <>
              <SelectedTitle>
                {selectedPerspective.custom_title || selectedPerspective.title}
              </SelectedTitle>
              {selectedPerspective.custom_title && (
                <SelectedDesc style={{ fontStyle: 'italic', marginBottom: 4 }}>
                  {t('topicFocus.original', { title: selectedPerspective.title })}
                </SelectedDesc>
              )}
              {selectedPerspective.description && <SelectedDesc>{selectedPerspective.description}</SelectedDesc>}
            </>
          )}
        </SelectedPerspective>
      )}
    </Card>
  );
}
